import { useMemo, useState } from 'react';
import clsx from 'clsx';

import { TextField } from './TextField';
import { SearchIcon } from '../icons';
import { useOutsideClick } from '../../hooks/useOutsideClick';

export interface ComboboxOption {
  value: string;
  label: string;
  subLabel?: string;
}

export interface SearchableComboboxProps {
  /** Currently selected value */
  value: string;
  /** Callback when selection changes */
  onChange: (value: string) => void;
  /** Array of options to display */
  options: ComboboxOption[];
  /** Placeholder text when searching */
  placeholder?: string;
  /** Label above the input */
  label?: string;
  /** ID for the input element */
  id?: string;
  /** Option to show at the top (e.g., "Base" or "All Partners") */
  defaultOption?: ComboboxOption;
  /** Custom filter function - defaults to label/subLabel match */
  filterFn?: (option: ComboboxOption, search: string) => boolean;
  /** Size of the input */
  size?: 'small' | 'large';
  /** Additional class names for the container */
  className?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
}

export function SearchableCombobox({
  value,
  onChange,
  options,
  placeholder = 'Search...',
  label,
  id,
  defaultOption,
  filterFn,
  size = 'small',
  className,
  disabled
}: SearchableComboboxProps) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const ref = useOutsideClick(() => {
    setIsOpen(false);
    setSearch('');
  });

  // Get display label for current selection
  const selectedLabel = useMemo(() => {
    if (defaultOption && value === defaultOption.value) {
      return defaultOption.label;
    }
    return options.find(opt => opt.value === value)?.label || '';
  }, [value, options, defaultOption]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    const searchLower = search.toLowerCase();
    const defaultFilter = (opt: ComboboxOption) =>
      opt.label.toLowerCase().includes(searchLower) ||
      opt.subLabel?.toLowerCase().includes(searchLower);

    const filter = filterFn
      ? (opt: ComboboxOption) => filterFn(opt, search)
      : defaultFilter;

    return options.filter(filter);
  }, [options, search, filterFn]);

  // Check if default option matches search
  const showDefaultOption = useMemo(() => {
    if (!defaultOption) return false;
    const searchLower = search.toLowerCase();
    return defaultOption.label.toLowerCase().includes(searchLower);
  }, [defaultOption, search]);

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div ref={ref} className={clsx('relative', className)}>
      {label && (
        <label htmlFor={id} className="mb-1 block text-sm text-gray-600">
          {label}
        </label>
      )}
      <TextField
        id={id}
        size={size}
        placeholder={placeholder}
        value={isOpen ? search : selectedLabel}
        onChange={e => {
          setSearch(e.target.value);
          if (!isOpen) setIsOpen(true);
        }}
        onFocus={() => {
          if (!disabled) {
            setIsOpen(true);
            setSearch('');
          }
        }}
        contentAfter={
          <div className="text-light-gray">
            <SearchIcon />
          </div>
        }
        disabled={disabled}
      />
      {isOpen && !disabled && (
        <div className="absolute top-full z-20 mt-1 w-full">
          <div className="text-input-shadow flex max-h-60 w-full flex-col overflow-y-auto rounded-lg bg-white">
            {showDefaultOption && defaultOption && (
              <button
                type="button"
                className={clsx(
                  'px-3 py-2 text-left hover:bg-gray-100',
                  value === defaultOption.value && 'bg-light-blue'
                )}
                onClick={() => handleSelect(defaultOption.value)}
              >
                {defaultOption.label}
              </button>
            )}
            {filteredOptions.length > 0
              ? filteredOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    className={clsx(
                      'px-3 py-2 text-left hover:bg-gray-100',
                      value === option.value && 'bg-light-blue'
                    )}
                    onClick={() => handleSelect(option.value)}
                  >
                    <div className="font-medium">{option.label}</div>
                    {option.subLabel && (
                      <div className="text-xs text-gray-500">
                        {option.subLabel}
                      </div>
                    )}
                  </button>
                ))
              : !showDefaultOption && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No results found
                  </div>
                )}
          </div>
        </div>
      )}
    </div>
  );
}
